/**
 * Active Directory findings — reading the `ad_analysis` engine.
 *
 * A compromised domain controller in a backup is not "one server with a problem". Restore it
 * and the backdoor comes back with it, domain-wide. That is the only reason this engine gets
 * its own section instead of 27 more rows in a list of hundreds.
 *
 * ── detection_class drives colour. severity never does ───────────────────────────────────
 *
 * The trap this module exists to prevent: ESC6 (a certificate template that lets a requester
 * supply their own SAN) is severity=critical but detection_class=indicator, deliberately. It
 * is a domain-takeover MISCONFIGURATION, not evidence that anyone used it. Colouring it red
 * because the severity says critical would contradict the decision-support model the whole
 * report is built on — the scan advises, it does not pronounce.
 *
 * Exactly one finding type is ever `confirmed`: the GPP cleartext credential (MS14-025).
 * Microsoft published the decryption key, so the credential really is recoverable from the
 * backup. Everything else is evidence to investigate.
 */

export type DetectionClass = 'confirmed' | 'indicator' | 'telemetry' | 'posture' | 'advisory'

export interface AdFinding {
    finding_id?: number | string | null
    scan_timestamp?: string | Date | null
    title?: string | null
    description?: string | null
    detection_class?: string | null
    severity?: string | null
    confidence?: string | null
    filepath?: string | null
    details?: Record<string, any> | null
}

/** Evidence source. This is the axis the section is laid out on. */
export type AdSource = 'sysvol' | 'eventlog' | 'registry' | 'adcs' | 'netlogon' | 'ntds' | 'coverage' | 'unknown'

export const AD_SOURCE_ORDER: AdSource[] = ['sysvol', 'eventlog', 'ntds', 'adcs', 'netlogon', 'registry', 'unknown', 'coverage']

export const AD_SOURCE_LABELS: Record<AdSource, { label: string; blurb: string }> = {
    sysvol: { label: 'Group Policy and SYSVOL', blurb: 'Policy files readable by any domain user.' },
    eventlog: { label: 'Security event log', blurb: 'What the domain controller recorded happening.' },
    ntds: { label: 'Directory database', blurb: 'Account and delegation state read from NTDS.dit.' },
    adcs: { label: 'Certificate Services', blurb: 'Certificate templates and CA configuration.' },
    netlogon: { label: 'Netlogon and Zerologon', blurb: 'Secure-channel enforcement.' },
    registry: { label: 'Host posture', blurb: 'Registry settings that weaken credential protection.' },
    unknown: { label: 'Source not recorded', blurb: 'The finding carries no evidence payload.' },
    coverage: { label: 'Not analysed', blurb: 'Checks that could not run.' },
}

/**
 * Which evidence source a finding came from, decided by the keys in `details`.
 *
 * Ordered most-specific first, because the shapes overlap: a SYSVOL finding and an NTDS one
 * both carry `account`, and only the presence of the GPO-specific keys separates them. The
 * registry findings are the residue — they carry `attack` and nothing else, which is the
 * documented contract for that group rather than a guess.
 */
export function adSourceOf(f: AdFinding): AdSource {
    const d = f?.details || {}
    const has = (k: string) => d[k] !== undefined && d[k] !== null

    if (has('coverage_gap')) return 'coverage'
    if (has('event_id')) return 'eventlog'
    if (has('ca') || has('edit_flags')) return 'adcs'
    if (has('accounts')) return 'netlogon'
    if (has('recovered') || has('privilege') || has('principal') || has('command')) return 'sysvol'
    if (f?.filepath) return 'sysvol'
    if (has('account')) return 'ntds'

    // `attack` and nothing else is the documented shape of a registry-posture finding. A
    // finding with NO details at all is a different thing and must not borrow that label:
    // real rows exist with details=null (all ten AD2 DCSync rows), and filing them under
    // "Host posture" would be the UI inventing an evidence source the scanner never claimed.
    if (has('attack')) return 'registry'
    return 'unknown'
}

/**
 * The account a finding is about, or null when it does not name one.
 *
 * The engine has emitted a literal '?' when it could not identify the subject. That is a
 * placeholder, not an account, and rendering it as one puts a question mark in a column where
 * operators expect a name. Treated as absent, and shown as "unattributed".
 */
export function accountOf(f: AdFinding): string | null {
    const raw = f?.details?.account
    if (raw == null) return null
    const s = String(raw).trim()
    if (!s || s === '?' || s === '-' || s.toLowerCase() === 'unknown') return null
    return s
}

/**
 * The MITRE technique, split out of `details.attack` ("T1003.006 DCSync").
 *
 * This is the single most useful field for an operator, so it is rendered as the headline
 * rather than buried in a details blob.
 */
export function attackOf(f: AdFinding): { id: string; name: string | null } | null {
    const raw = String(f?.details?.attack ?? '').trim()
    if (!raw) return null
    const m = raw.match(/^(T\d{4}(?:\.\d{3})?)\s*(.*)$/i)
    if (!m) return { id: raw, name: null }
    const name = m[2].trim()
    return { id: m[1].toUpperCase(), name: name || null }
}

/**
 * Honour the stored detection_class and nothing else.
 *
 * No severity fallback on purpose. An unrecognised value degrades to `indicator`, which is the
 * safe direction: it neither claims a confirmed detection nor files evidence away as telemetry.
 */
export function adDetectionClass(f: AdFinding): DetectionClass {
    const dc = String(f?.detection_class || '').toLowerCase()
    // posture (scanner df8a055): a configuration weakness that predates any incident. Filing it as
    // an indicator is the exact conflation the class was introduced to end.
    //
    // advisory (scanner 2270c27): an interpretation or an observed change, not a detection. The AD
    // engine does not produce it today - only the AI and the config diff do - but the class is read
    // from the finding, never assumed from the engine, so one that arrives is not filed as amber.
    return dc === 'confirmed' || dc === 'telemetry' || dc === 'posture' || dc === 'advisory' ? dc : 'indicator'
}

export function isCoverageGap(f: AdFinding): boolean {
    return adSourceOf(f) === 'coverage'
}

/**
 * The remediation a finding implies, where restoring the object without it would restore the
 * problem too. Silent when there is nothing specific to say — a generic "investigate" line on
 * every row would train the reader to skip all of them.
 */
export function recoveryActionFor(f: AdFinding): string | null {
    const attack = attackOf(f)?.id || ''
    const hay = `${f?.title || ''} ${attack}`.toLowerCase()

    if (hay.includes('gpp') || hay.includes('ms14-025') || f?.details?.recovered != null) {
        return 'This credential is recoverable from the backup — Microsoft published the key. Rotate it before the object is restored, not after.'
    }
    if (hay.includes('krbtgt')) {
        return 'Rotate krbtgt twice, allowing full replication between the two rotations. A single rotation leaves existing forged tickets valid.'
    }
    if (hay.includes('esc6') || hay.includes('esc8') || String(f?.details?.edit_flags ?? '').length > 0) {
        return 'Clear the template flag on the issuing CA before recovery, or the restored CA will issue the same certificates again.'
    }
    if (hay.includes('zerologon') || f?.details?.accounts != null) {
        return 'Re-enable secure-channel enforcement and remove the exemptions before this controller rejoins the domain.'
    }
    if (hay.includes('dsrm')) {
        return 'Reset the DSRM password and disable DSRM network logon on the restored controller.'
    }
    if (hay.includes('unconstrained delegation')) {
        return 'Remove unconstrained delegation before recovery — a restored host with it can capture TGTs for any account that authenticates to it.'
    }
    if (hay.includes('sid history')) {
        return 'Clear SID history on the affected account before recovery; it grants the privileges of the historical SID.'
    }
    return null
}

/**
 * One observation, however many times it was recorded.
 *
 * Event-derived findings are inherently repetitive: a domain controller exercises the same
 * rights continuously, so one behaviour becomes N identical rows. AD2 currently carries ten
 * byte-identical DCSync rows, and rendering ten rows is poor even when the findings are real —
 * the LENGTH of the list is what a reader perceives as severity.
 *
 * Collapsed on (title, detection_class, account) rather than on the whole finding, because the
 * timestamp is the only field that legitimately differs between recordings of one observation.
 */
export interface AdCluster {
    key: string
    title: string
    detectionClass: DetectionClass
    /** null when the engine could not attribute it — rendered as "unattributed", never "?". */
    account: string | null
    count: number
    first: Date | null
    last: Date | null
    /** Members are identical apart from time; this one carries the detail to render. */
    sample: AdFinding
    members: AdFinding[]
}

const ts = (f: AdFinding): Date | null => {
    if (!f?.scan_timestamp) return null
    const d = new Date(f.scan_timestamp)
    return Number.isNaN(d.getTime()) ? null : d
}

export function collapseAdFindings(findings: AdFinding[]): AdCluster[] {
    const by = new Map<string, AdCluster>()
    for (const f of findings || []) {
        const title = String(f?.title || 'Untitled finding')
        const cls = adDetectionClass(f)
        const account = accountOf(f)
        const key = `${title}|${cls}|${account ?? ''}`
        const t = ts(f)

        const c = by.get(key)
        if (!c) {
            by.set(key, { key, title, detectionClass: cls, account, count: 1, first: t, last: t, sample: f, members: [f] })
            continue
        }
        c.count += 1
        c.members.push(f)
        if (t) {
            if (!c.first || t < c.first) c.first = t
            if (!c.last || t > c.last) c.last = t
        }
    }
    return [...by.values()]
}

export interface AdGroup {
    source: AdSource
    label: string
    blurb: string
    clusters: AdCluster[]
    /** Total findings behind the clusters — collapsing must never hide the real count. */
    findingCount: number
}

export function groupAdBySource(findings: AdFinding[]): AdGroup[] {
    const by = new Map<AdSource, AdFinding[]>()
    for (const f of findings || []) {
        const s = adSourceOf(f)
        if (!by.has(s)) by.set(s, [])
        by.get(s)!.push(f)
    }
    return AD_SOURCE_ORDER
        .filter((s) => by.has(s))
        .map((s) => ({
            source: s,
            label: AD_SOURCE_LABELS[s].label,
            blurb: AD_SOURCE_LABELS[s].blurb,
            clusters: collapseAdFindings(by.get(s)!),
            findingCount: by.get(s)!.length,
        }))
}

/**
 * The four states this section can be in.
 *
 * The brief specifies three, and they are all here. The fourth, `not-reported`, is what the
 * data actually supports today and it is NOT a collapsed version of the others.
 *
 * Telling "the engine stood down because this is not a domain controller" apart from "the
 * engine ran and the AD audit was clean" requires the engine to say which. It reports that in
 * the `engines` block. Across the last 60 scan reports in ordb no AD entry appears under any
 * spelling, so for a report produced before the engine shipped both states are unknowable —
 * and claiming either would be a lie in the direction the whole report is built to avoid.
 * So: no entry and no findings means "did not report", said plainly.
 */
export type AdState = 'findings' | 'clean' | 'not-dc' | 'not-reported'

export interface AdAudit {
    state: AdState
    /** Findings excluding the coverage-gap marker, which is rendered as a gap not a finding. */
    findings: AdFinding[]
    groups: AdGroup[]
    coverageGap: AdFinding | null
    counts: Record<DetectionClass, number>
    /** true / false when the engine said so, null when nothing in the report says either way. */
    isDomainController: boolean | null
}

// The engines block is PascalCase ("BinaryAnalysis") while findings are snake_case
// ("binary_analysis"). The AD engine has never appeared in a real report, so every plausible
// spelling is accepted rather than betting on one.
const AD_ENGINE_KEYS = ['ADAnalysis', 'AdAnalysis', 'ActiveDirectory', 'AD', 'ad_analysis', 'ADAudit']

function adEngineEntry(engines: any): any | null {
    if (!engines || typeof engines !== 'object') return null
    for (const k of AD_ENGINE_KEYS) if (engines[k]) return engines[k]
    // Last resort: any key that looks like an AD engine.
    for (const [k, v] of Object.entries(engines)) {
        if (/^(ad|activedirectory)/i.test(k.replace(/[_\s-]/g, ''))) return v
    }
    return null
}

export function adAudit(report: any): AdAudit {
    const raw: AdFinding[] = Array.isArray(report?.ad_findings) ? report.ad_findings : []
    const coverageGap = raw.find(isCoverageGap) || null
    const findings = raw.filter((f) => !isCoverageGap(f))

    const counts: Record<DetectionClass, number> = { confirmed: 0, indicator: 0, telemetry: 0, posture: 0, advisory: 0 }
    for (const f of findings) counts[adDetectionClass(f)] += 1

    const entry = adEngineEntry(report?.engines)
    const status = String(entry?.status || '').toLowerCase()
    const reason = String(entry?.reason || entry?.error || '').toLowerCase()
    const saysNotDc = /domain controller|not a dc|no ntds|not promoted/.test(reason)

    let isDomainController: boolean | null = null
    if (entry) {
        if (saysNotDc) isDomainController = false
        else if (status === 'complete' || status === 'completed') isDomainController = true
    }
    // Findings can only exist for a domain controller, whatever the engines block says.
    if (raw.length > 0) isDomainController = true

    let state: AdState
    if (findings.length > 0 || coverageGap) state = 'findings'
    else if (isDomainController === false) state = 'not-dc'
    else if (isDomainController === true) state = 'clean'
    else state = 'not-reported'

    return { state, findings, groups: groupAdBySource(findings), coverageGap, counts, isDomainController }
}
